import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BLOCKS_PER_SLIDE_CAP } from '../constants'
import * as presentationService from '../services/presentationService'
import type { DeckDefinition, EntityRef, PreviewBlockInput, ResolvedBlock } from '../types/presentation.types'
import { stableStringify } from '../utils/deckOps'

/**
 * Resolved content for the blocks on some slides of a deck (MYD-493).
 *
 * ## What is cached, and why that gives invalidation for free
 *
 * A block's content depends on three things: its element, its effective
 * source (its own, else its story's anchor) and its config. The cache is keyed
 * by a hash of exactly those three, not by block id. So:
 *
 *  - editing a block's config produces a new hash, which is a cache miss, which
 *    fetches. Nothing has to remember to invalidate anything;
 *  - undoing that edit returns to the old hash, which is still cached, so undo
 *    is instant;
 *  - two blocks showing the same thing share one entry.
 *
 * ## Batching
 *
 * Misses are fetched through `resolve-preview`, one request per slide. The
 * editor's deck is usually ahead of what is saved, so resolving the STORED
 * deck by id would show stale content; resolve-preview takes the blocks as
 * they are now. Its limit of six blocks per request is the per-slide cap, so a
 * slide is always exactly one request.
 *
 * ## Freshness
 *
 * Entries do not expire on their own: a slide should not flicker back to
 * loading while someone is editing it. `refresh()` drops the cache, for when
 * the underlying data is known to have changed.
 */

/** A block whose request has not come back yet. */
export type BlockView = ResolvedBlock | { status: 'loading'; payload: null }

const LOADING: BlockView = { status: 'loading', payload: null }

const failed = (): ResolvedBlock => ({ status: 'error', payload: null, message: 'This block could not be loaded.' })

interface Wanted {
	blockId: string
	slideId: string
	hash: string
	request: Omit<PreviewBlockInput, 'id'>
}

/** The cache key for a block: what it shows, not which block it is. */
export const blockHash = (element: string, source: EntityRef | null, config: Record<string, unknown>) =>
	stableStringify({ element, source, config })

const collectWanted = (deck: DeckDefinition, slideIds: string[]): Wanted[] => {
	const wanted = new Set(slideIds)
	const out: Wanted[] = []

	for (const story of deck.stories) {
		for (const slide of story.slides) {
			if (!wanted.has(slide.id)) continue
			for (const block of slide.blocks) {
				const source = block.source ?? story.anchor ?? null
				out.push({
					blockId: block.id,
					slideId: slide.id,
					hash: blockHash(block.element, source, block.config ?? {}),
					request: { element: block.element, config: block.config ?? {}, source },
				})
			}
		}
	}

	return out
}

export interface UseResolvedBlocks {
	/** Keyed by BLOCK id, for every block on the requested slides. */
	blocks: Record<string, BlockView>
	/** Drop everything cached and fetch the requested slides again. */
	refresh: () => void
}

/**
 * @param deck     the deck as it is now (usually `useDeckEditor().deck`)
 * @param slideIds the slides to resolve: typically the one on the canvas plus
 *                 its neighbours. Blocks on other slides are not fetched.
 */
export function useResolvedBlocks(deck: DeckDefinition, slideIds: string[]): UseResolvedBlocks {
	const [cache, setCache] = useState<Record<string, ResolvedBlock>>({})

	// Hashes with a request in flight, so a re-render does not ask again.
	const pending = useRef(new Set<string>())
	// Bumped by refresh(). A response from before a refresh is discarded rather
	// than repopulating the cache that refresh just emptied.
	const generation = useRef(0)

	const slideKey = slideIds.join('|')
	const wanted = useMemo(() => collectWanted(deck, slideIds), [deck, slideKey]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		// Group misses by slide. A hash already claimed by an earlier slide is
		// not requested twice in the same pass.
		const bySlide = new Map<string, Wanted[]>()
		const claimed = new Set<string>()
		for (const item of wanted) {
			if (cache[item.hash] || pending.current.has(item.hash) || claimed.has(item.hash)) continue
			claimed.add(item.hash)
			const group = bySlide.get(item.slideId) ?? []
			group.push(item)
			bySlide.set(item.slideId, group)
		}
		if (bySlide.size === 0) return

		const requestGeneration = generation.current

		for (const group of bySlide.values()) {
			// A slide never holds more than the cap, but a stored deck from an
			// older release might; chunk rather than 400 the whole slide.
			for (let start = 0; start < group.length; start += BLOCKS_PER_SLIDE_CAP) {
				const chunk = group.slice(start, start + BLOCKS_PER_SLIDE_CAP)
				chunk.forEach(item => pending.current.add(item.hash))

				// Short positional ids: the hash itself is far too long to send.
				const blocks = chunk.map((item, index) => ({ id: `b${index}`, ...item.request }))

				presentationService
					.resolvePreview(blocks)
					.then(
						response => chunk.map((item, index) => [item.hash, response.blocks[`b${index}`] ?? failed()] as const),
						// A failed request is cached as an error rather than
						// retried, or a persistent failure would loop forever.
						// refresh() is the way to try again.
						() => chunk.map(item => [item.hash, failed()] as const),
					)
					.then(entries => {
						entries.forEach(([hash]) => pending.current.delete(hash))
						if (generation.current !== requestGeneration) return
						setCache(previous => ({ ...previous, ...Object.fromEntries(entries) }))
					})
			}
		}
	}, [wanted, cache])

	const refresh = useCallback(() => {
		generation.current += 1
		pending.current.clear()
		setCache({})
	}, [])

	const blocks = useMemo(() => {
		const views: Record<string, BlockView> = {}
		for (const item of wanted) views[item.blockId] = cache[item.hash] ?? LOADING
		return views
	}, [wanted, cache])

	return useMemo(() => ({ blocks, refresh }), [blocks, refresh])
}

export default useResolvedBlocks
